// React binding for the segment-lifecycle stubs, via TanStack Query against the
// async SegmentService. The hook's return shape is unchanged from before, so
// its consumers don't care that reads/writes are now asynchronous — and when
// SegmentService swaps localStorage for a real backend, nothing here changes.

import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AudienceConfiguration } from '@/lib/domain'
import type {
    ConfigSlotRef,
    ConfigValues,
    SavedConfig,
} from '@/lib/marketplaceDestinations'
import { useConfig } from '@/app/config'
import { useAuthedFetch } from '@/hooks/useAuthedFetch'
import { useSegmentService } from './SegmentServiceProvider'
import {
    clearCachedInsights,
    emptyStore,
    fetchAudienceInsights,
    getCachedInsights,
    insightsCacheFresh,
    insightsFingerprint,
    lifecycleFor,
    type CachedInsights,
    refreshAudienceInsights,
    savedConfigFromStore,
    segmentKey,
    setCachedInsights,
} from './api'
import type { BuildDistributionsParams, DemoRunHistoryArgs } from './api'
import type {
    Destination,
    Distribution,
    Modeling,
    SegmentKey,
    SegmentLifecycle,
} from './types'

const SEGMENT_STUBS_KEY = ['segment-stubs']

export type SetSegmentDistributionsArgs = Omit<BuildDistributionsParams, 'now'>

export interface UseSegmentStubs {
    destinations: Destination[]
    lifecycleFor: (key: SegmentKey, threadId: string) => SegmentLifecycle
    saveModeling: (modeling: Modeling) => void
    setSegmentDistributions: (args: SetSegmentDistributionsArgs) => void
    setDistribution: (id: string, patch: Partial<Distribution>) => void
    removeDistribution: (id: string) => void
    addDestination: (destination: Destination) => void
    removeDestination: (id: string) => void
    // Hidden demo affordance: generate a realistic run history for a delivery.
    addDemoRunHistory: (args: DemoRunHistoryArgs) => void
    // Lazily-collected destination config: accessors over saved partial values,
    // and a merge-save for one slot.
    savedConfig: SavedConfig
    saveDestinationConfig: (ref: ConfigSlotRef, values: ConfigValues) => void
}

export function useSegmentStubs(): UseSegmentStubs {
    const service = useSegmentService()
    const queryClient = useQueryClient()

    const { data } = useQuery({
        queryKey: SEGMENT_STUBS_KEY,
        queryFn: () => service.fetchStore(),
    })
    // Empty store while the first fetch is in flight; derivations degrade
    // gracefully (no modeling, not distributed) until data arrives.
    const store = data ?? emptyStore()

    const invalidate = useCallback(
        () => queryClient.invalidateQueries({ queryKey: SEGMENT_STUBS_KEY }),
        [queryClient]
    )

    const { mutate: saveModeling } = useMutation({
        mutationFn: (modeling: Modeling) => service.saveModeling(modeling),
        onSuccess: invalidate,
    })
    const { mutate: setSegmentDistributions } = useMutation({
        mutationFn: (args: SetSegmentDistributionsArgs) =>
            service.setSegmentDistributions(args),
        onSuccess: invalidate,
    })
    const { mutate: setDistributionMutate } = useMutation({
        mutationFn: (vars: { id: string; patch: Partial<Distribution> }) =>
            service.setDistribution(vars.id, vars.patch),
        onSuccess: invalidate,
    })
    const { mutate: removeDistributionMutate } = useMutation({
        mutationFn: (id: string) => service.removeDistribution(id),
        onSuccess: invalidate,
    })
    const { mutate: addDestinationMutate } = useMutation({
        mutationFn: (destination: Destination) =>
            service.addDestination(destination),
        onSuccess: invalidate,
    })
    const { mutate: removeDestinationMutate } = useMutation({
        mutationFn: (id: string) => service.removeDestination(id),
        onSuccess: invalidate,
    })
    const { mutate: addDemoRunHistoryMutate } = useMutation({
        mutationFn: (args: DemoRunHistoryArgs) =>
            service.addDemoRunHistory(args),
        onSuccess: invalidate,
    })
    const { mutate: saveDestinationConfigMutate } = useMutation({
        mutationFn: (vars: { ref: ConfigSlotRef; values: ConfigValues }) =>
            service.setDestinationConfig(vars.ref, vars.values),
        onSuccess: invalidate,
    })

    const lifecycle = useCallback(
        (key: SegmentKey, threadId: string) =>
            lifecycleFor(store, threadId, key),
        [store]
    )
    const setDistribution = useCallback(
        (id: string, patch: Partial<Distribution>) =>
            setDistributionMutate({ id, patch }),
        [setDistributionMutate]
    )
    const removeDistribution = useCallback(
        (id: string) => removeDistributionMutate(id),
        [removeDistributionMutate]
    )
    const addDestination = useCallback(
        (destination: Destination) => addDestinationMutate(destination),
        [addDestinationMutate]
    )
    const removeDestination = useCallback(
        (id: string) => removeDestinationMutate(id),
        [removeDestinationMutate]
    )
    const addDemoRunHistory = useCallback(
        (args: DemoRunHistoryArgs) => addDemoRunHistoryMutate(args),
        [addDemoRunHistoryMutate]
    )
    const savedConfig = useMemo(() => savedConfigFromStore(store), [store])
    const saveDestinationConfig = useCallback(
        (ref: ConfigSlotRef, values: ConfigValues) =>
            saveDestinationConfigMutate({ ref, values }),
        [saveDestinationConfigMutate]
    )

    return {
        destinations: store.destinations,
        lifecycleFor: lifecycle,
        saveModeling,
        setSegmentDistributions,
        setDistribution,
        removeDistribution,
        addDestination,
        removeDestination,
        addDemoRunHistory,
        savedConfig,
        saveDestinationConfig,
    }
}

// Real "what this audience buys" insight charts for any segment (transaction,
// user-attribute, or crossed). Cache-first, three tiers (data refreshes weekly on
// Monday):
//   • cache hit, still this data-week → serve cached bars, no network call;
//   • cache hit, a Monday boundary has passed → replay the cached searches live
//     via the cheap no-LLM /refresh endpoint (and re-cache with a new timestamp);
//   • miss / stale targeting → rebuild with the type's LLM endpoint and cache it.
// The fingerprint (targeting hash) is in the key so a segment edit rebuilds. The
// per-type LLM endpoint is chosen inside fetchAudienceInsights; the cache, refresh,
// and freshness logic are all type-agnostic.
export const useAudienceInsights = (config: AudienceConfiguration) => {
    const { audienceServiceUrl } = useConfig()
    const authedFetch = useAuthedFetch()
    const key = segmentKey(config)
    const fingerprint = insightsFingerprint(config)
    const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
        queryKey: ['audienceInsights', key, fingerprint],
        queryFn: async (): Promise<CachedInsights> => {
            const nowMs = Date.now()
            const nowIso = new Date(nowMs).toISOString()
            const cached = getCachedInsights(key, fingerprint)
            if (cached && cached.charts.length > 0) {
                // Within the current data-week: the cached bars are current.
                if (insightsCacheFresh(cached.fetchedAt, nowMs)) {
                    return cached
                }
                // A weekly (Monday) refresh has happened — re-run the cached
                // searches live. The layout is unchanged, so generatedAt carries
                // over; only fetchedAt advances. Re-cache only a non-empty result
                // so a transient empty refresh doesn't wipe a good cache.
                const fresh = await refreshAudienceInsights(
                    audienceServiceUrl,
                    authedFetch,
                    cached.charts
                )
                if (fresh.length > 0) {
                    const meta = {
                        fetchedAt: nowIso,
                        generatedAt: cached.generatedAt,
                    }
                    setCachedInsights(key, fingerprint, fresh, meta)
                    return { charts: fresh, ...meta }
                }
                return cached
            }
            // Build the layout with the LLM — generatedAt and fetchedAt are both now.
            const built = await fetchAudienceInsights(
                audienceServiceUrl,
                authedFetch,
                config
            )
            const meta = { fetchedAt: nowIso, generatedAt: nowIso }
            setCachedInsights(key, fingerprint, built, meta)
            return { charts: built, ...meta }
        },
        enabled: !!config.dataset_id,
    })

    // Force a fresh LLM rebuild: drop the cache so the queryFn takes the LLM
    // path, then refetch. Used by the "Insights created by AI …" action.
    const regenerate = useCallback(() => {
        clearCachedInsights(key)
        return refetch()
    }, [key, refetch])

    return { data, isLoading, isError, error, isFetching, regenerate }
}

// Drop a segment's cached insights (localStorage + the in-memory query). Used
// when a segment is removed from a cart so its stale entry doesn't linger.
export const useClearInsightsCache = () => {
    const queryClient = useQueryClient()
    return useCallback(
        (
            config: Pick<
                AudienceConfiguration,
                'dataset_type' | 'segment_id' | 'dataset_id'
            >
        ) => {
            const key = segmentKey(config)
            clearCachedInsights(key)
            queryClient.removeQueries({
                queryKey: ['audienceInsights', key],
            })
        },
        [queryClient]
    )
}
